import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Helper function to generate a reference number
const generateRefNo = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  
  return `REF-${year}${month}${day}-${hours}${minutes}${seconds}-${random}`;
};

interface EmailCommunication {
  id: number;
  date: string;
  sent_to: string;
  subject?: string;
  message: string;
  employee: string;
  delivery_status: 'sent' | 'delivered' | 'failed' | 'pending';
  ref_no: string;
  forward: string;
  metadata?: {
    documents?: Array<{
      id: number;
      file_name: string;
      original_name: string;
    }>;
    attachment_count?: number;
  };
}

interface ClientEmail {
  id: number;
  email: string;
  is_primary: boolean;
  email_type: string;
}

interface PersonOfInterest {
  id: number;
  email: string;
  name: string;
  surname: string;
}

interface Quote {
  id: number;
  quote_number?: string;
  administrator_id?: number;
  underwriter_id?: number;
  created_at?: string;
  createdDate?: string;
  administrator_ids?: number[];
  underwriter_ids?: number[];
}

interface ClientDocument {
  id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  description: string;
  created_at: string;
  file_size: number;
  document_type: string;
  quote_id: number;
  file_type?: string;
}

interface ClientPersonalEmailTabProps {
  clientId: string;
  user?: {
    id?: number;
    username?: string;
    name?: string;
    email?: string;
  };
  instructionId?: string;
  instructionRef?: string;
  isInstructionContext?: boolean;
  clientName?: string;
  policyNumber?: string;
  onEmailChange?: () => void;
}

const ClientPersonalEmailTab: React.FC<ClientPersonalEmailTabProps> = ({ 
  clientId, 
  user,
  instructionId,
  instructionRef,
  isInstructionContext = false,
  clientName = '',
  policyNumber = '',
  onEmailChange
}) => {
  const [emails, setEmails] = useState<EmailCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailCommunication | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string; type: string }[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  
  // Documents state
  const [clientDocuments, setClientDocuments] = useState<ClientDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  const [formData, setFormData] = useState({
    sent_to: '',
    create_recipient: '',
    subject: '',
    message: '',
    selectedDocuments: [] as number[],
    instruction_id: instructionId || ''
  });

  const [recipients, setRecipients] = useState<string[]>([]);

  const [selectedForForward, setSelectedForForward] = useState<number[]>([]);
  const [loggedInUser, setLoggedInUser] = useState<string>('System');
  const [forwardAttachments, setForwardAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{current: number; total: number} | null>(null);

  const MAX_DOCUMENTS = 5;
  const MAX_FILE_SIZE_MB = 5;
  const MAX_PROCESSING_TIME = 60000; // 60 seconds

  // Fetch documents whenever clientId changes - CLEAR FIRST
  useEffect(() => {
    if (clientId) {
      // Clear previous documents immediately to prevent showing old data
      setClientDocuments([]);
      setFormData(prev => ({ ...prev, selectedDocuments: [] }));
      fetchClientDocuments();
    }
  }, [clientId]);

  // Fetch emails and email options
  useEffect(() => {
    fetchEmails();
    fetchEmailOptions();
  }, [clientId]);

  // Get logged-in user
  useEffect(() => {
    // First try to use user from props
    if (user) {
      console.log('📋 User from props:', user);
      const userName = user.username || user.name || user.email;
      if (userName) {
        console.log('✅ Using username from props:', userName);
        setLoggedInUser(userName);
        return;
      }
    }

    const getUserName = () => {
      // Try multiple sources for user data
      const storedUser = localStorage.getItem('user');
      const storedUserData = localStorage.getItem('userData');
      const storedAuth = localStorage.getItem('auth');

      // Debug: Log all localStorage keys to see what's available
      console.log('🔍 All localStorage keys:', Object.keys(localStorage));
      console.log('🔍 localStorage contents:', {
        user: localStorage.getItem('user'),
        userData: localStorage.getItem('userData'),
        auth: localStorage.getItem('auth'),
        token: localStorage.getItem('token') ? 'present' : 'missing'
      });
      
      console.log('🔍 Looking for user data in localStorage...');
      
      // Try 'user' key first
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          console.log('📋 user data:', userData);
          
          // Try multiple possible field names
          const userName = userData.username || 
                          userData.name || 
                          userData.email || 
                          userData.fullName || 
                          userData.displayName ||
                          userData.user?.username ||
                          userData.user?.name ||
                          userData.user?.email;
          
          if (userName) {
            console.log('✅ Found username from "user" key:', userName);
            setLoggedInUser(userName);
            return;
          }
          
          // If no name found but we have an email, use email prefix
          if (userData.email) {
            const emailPrefix = userData.email.split('@')[0];
            console.log('✅ Using email prefix as username:', emailPrefix);
            setLoggedInUser(emailPrefix);
            return;
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
      
      // Try 'userData' key
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          console.log('📋 userData:', userData);
          
          const userName = userData.username || 
                          userData.name || 
                          userData.email || 
                          userData.fullName ||
                          userData.displayName;
          
          if (userName) {
            console.log('✅ Found username from "userData" key:', userName);
            setLoggedInUser(userName);
            return;
          }
        } catch (error) {
          console.error('Error parsing userData:', error);
        }
      }
      
      // Try 'auth' key
      if (storedAuth) {
        try {
          const authData = JSON.parse(storedAuth);
          console.log('📋 auth data:', authData);
          
          const userName = authData.user?.username || 
                          authData.user?.name || 
                          authData.user?.email || 
                          authData.username || 
                          authData.email;
          
          if (userName) {
            console.log('✅ Found username from "auth" key:', userName);
            setLoggedInUser(userName);
            return;
          }
        } catch (error) {
          console.error('Error parsing auth data:', error);
        }
      }
      
      // Try to get from the API (if user is authenticated)
      const token = localStorage.getItem('token');
      if (token) {
        // Try to decode JWT token to get user info
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('📋 JWT payload:', payload);
            
            const userName = payload.username || 
                            payload.name || 
                            payload.email || 
                            payload.sub ||
                            payload.user?.username ||
                            payload.user?.name;
            
            if (userName) {
              console.log('✅ Found username from JWT token:', userName);
              setLoggedInUser(userName);
              return;
            }
          }
        } catch (error) {
          console.error('Error decoding JWT:', error);
        }
      }
      
      // If all else fails, check if we have a user in the API response
      const userFromApi = localStorage.getItem('userFromApi');
      if (userFromApi) {
        try {
          const userData = JSON.parse(userFromApi);
          const userName = userData.username || userData.name || userData.email;
          if (userName) {
            console.log('✅ Found username from API:', userName);
            setLoggedInUser(userName);
            return;
          }
        } catch (error) {
          console.error('Error parsing userFromApi:', error);
        }
      }
      
      // Final fallback - use 'System' and log warning
      console.warn('⚠️ Could not find logged-in user data, using "System" as fallback');
      setLoggedInUser('System');
    };
    
    getUserName();
  }, [user]);

  // Set subject when instruction context changes
  useEffect(() => {
    if (isInstructionContext && instructionRef) {
      setFormData(prev => ({
        ...prev,
        subject: `[${instructionRef}] `
      }));
    }
  }, [isInstructionContext, instructionRef]);

  // Debug: Log when instructionRef changes
  useEffect(() => {
    console.log('📝 instructionRef changed:', instructionRef);
    console.log('📝 isInstructionContext:', isInstructionContext);
    if (isInstructionContext && instructionRef) {
      console.log('✅ Should set subject to:', `[${instructionRef}] `);
    }
  }, [instructionRef, isInstructionContext]);

  const fetchEmails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      let url = `${process.env.REACT_APP_API_URL}/api/email/communications/${clientId}`;

      // If this is an instruction context, filter by instruction_id
      if (isInstructionContext && instructionId) {
        url = `${process.env.REACT_APP_API_URL}/api/email/communications/${clientId}?instruction_id=${instructionId}`;
      }

      const response = await axios.get(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEmails(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching email communications:', err);
      setError('Failed to load email communications');
      setEmails([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateDocumentExists = async (docId: number, token: string): Promise<boolean> => {
  try {
    // Use the appropriate endpoint based on context
    const endpoint = isInstructionContext 
      ? `${process.env.REACT_APP_API_URL}/api/instruction-documents/download/${docId}`
      : `${process.env.REACT_APP_API_URL}/api/client-documents/download/${docId}`;
      
    const headResponse = await axios.head(
      endpoint,
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000 // 5 second timeout
      }
    );
    return headResponse.status === 200;
  } catch (err) {
    return false;
  }
};

  const fetchClientDocuments = async () => {
  try {
    setIsLoadingDocuments(true);
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found for fetching documents');
      setIsLoadingDocuments(false);
      return;
    }

    console.log(`📄 Fetching documents for client ID: ${clientId}`);
    console.log(`📄 isInstructionContext: ${isInstructionContext}, instructionId: ${instructionId}`);
    
    // Clear existing documents before fetching new ones
    setClientDocuments([]);
    
    let allDocuments: any[] = [];
    
    // ✅ If this is an instruction context, fetch documents for that instruction
    if (isInstructionContext && instructionId) {
      try {
        console.log(`📄 Fetching documents for instruction ID: ${instructionId}`);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/instruction-documents?instructionId=${instructionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // The response from your backend is an array directly
        const documents = Array.isArray(response.data) ? response.data : response.data.data || [];
        console.log(`✅ Found ${documents.length} documents for instruction ${instructionId}`);
        
        // For instruction documents, use the instruction-documents download endpoint
        // Map the documents to match the expected format
        const mappedDocuments = documents.map((doc: any) => ({
  id: doc.id,
  file_name: doc.file_name || doc.original_name || 'document.pdf',
  original_name: doc.original_name || doc.file_name || 'document.pdf',
  file_path: doc.file_path || '',
  description: doc.description || '',
  created_at: doc.created_at || new Date().toISOString(),
  file_size: doc.file_size || 0,
  document_type: doc.document_type || 'instruction',
  quote_id: doc.quote_id || 0,
  // Keep any other fields
  ...doc
}));
        
        // Set documents directly without validation since they're from instruction context
console.log('📄 Mapped instruction documents:', mappedDocuments);
console.log('📄 Document IDs:', mappedDocuments.map((d: any) => d.id));
console.log('📄 Document file names:', mappedDocuments.map((d: any) => d.file_name));
setClientDocuments(mappedDocuments);
setIsLoadingDocuments(false);
return;

      } catch (err) {
        console.warn(`Could not fetch documents for instruction ${instructionId}:`, err);
        // Fall through to client documents
      }
    }
    
    // If not in instruction context or no instruction documents found, fetch client documents
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/client-documents?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const documents = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log(`✅ Found ${documents.length} documents for client ${clientId} from client-documents endpoint`);
      allDocuments = [...documents];
    } catch (err) {
      console.warn(`Could not fetch from client-documents endpoint for client ${clientId}:`, err);
    }
    
    // Also try to get signed documents (proposals) as a fallback
    if (!isInstructionContext) {
      try {
        const signedResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/signed-documents?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const signedDocs = signedResponse.data.documents || signedResponse.data || [];
        console.log(`✅ Found ${signedDocs.length} signed documents for client ${clientId}`);
        
        const existingIds = new Set(allDocuments.map((d: any) => d.id));
        for (const doc of signedDocs) {
          if (!existingIds.has(doc.id)) {
            allDocuments.push(doc);
            existingIds.add(doc.id);
          }
        }
      } catch (err) {
        console.warn(`Could not fetch signed documents for client ${clientId}:`, err);
      }
    }
    
    // Filter out documents with invalid file paths or missing files
    const validDocuments = allDocuments.filter(doc => {
      // Check if document has a file path
      if (!doc.file_path || doc.file_path.trim() === '') {
        console.warn(`⚠️ Document ${doc.id} has no file path, filtering out`);
        return false;
      }
      
      // Check if document has a file name or original name
      if (!doc.file_name && !doc.original_name) {
        console.warn(`⚠️ Document ${doc.id} has no file name, filtering out`);
        return false;
      }
      
      return true;
    });

    console.log(`✅ Total unique documents found for client ${clientId}: ${allDocuments.length}`);
    console.log(`✅ Valid documents after filtering: ${validDocuments.length}`);

    // If there are valid documents, check if they actually exist on the server
    if (validDocuments.length > 0) {
      console.log(`🔍 Checking if documents exist on server...`);
      const existenceChecks = await Promise.all(
        validDocuments.map(async (doc) => {
          const exists = await validateDocumentExists(doc.id, token);
          if (!exists) {
            console.warn(`⚠️ Document ${doc.id} (${doc.original_name || doc.file_name}) does not exist on server, will be filtered out`);
          }
          return { ...doc, exists };
        })
      );
      
      // Filter to only documents that exist on the server
      const existingDocuments = existenceChecks.filter(doc => doc.exists);
      console.log(`✅ Documents that exist on server: ${existingDocuments.length}`);
      
      // Remove the 'exists' property before setting state
      const documentsToSet = existingDocuments.map(({ exists, ...doc }) => doc);
      setClientDocuments(documentsToSet);
    } else {
      setClientDocuments([]);
    }
  } catch (err) {
    console.error('Error fetching client documents:', err);
    setClientDocuments([]);
  } finally {
    setIsLoadingDocuments(false);
  }
};

  const getEmailFromAdmin = (admin: any): string | null => {
    if (!admin) return null;
    
    const possibleEmailFields = [
      'quotationsEmail',
      'quotations_email',
      'consultantEmail',
      'consultant_email',
      'adminEmailAddress',
      'admin_email_address',
      'claimsEmail',
      'claims_email',
      'instructionsEmail',
      'instructions_email',
      'email',
      'email_address'
    ];
    
    for (const field of possibleEmailFields) {
      const value = admin[field];
      if (value && typeof value === 'string' && value.trim()) {
        console.log(`  ✅ Found email in field '${field}': ${value}`);
        return value;
      }
    }
    
    return null;
  };

  const getAdminName = (admin: any): string => {
    if (!admin) return '';
    return admin.consultantName || admin.consultant_name || admin.name || admin.administrator_name || '';
  };

  const fetchAdministratorDetails = async (adminId: number, token: string): Promise<any | null> => {
    try {
      console.log(`  Fetching administrator details for ID: ${adminId}`);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/administrators/${adminId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 200) {
        const admin = response.data.data || response.data;
        console.log(`  Administrator data:`, admin);
        return admin;
      }
    } catch (err) {
      console.log(`  Failed to fetch administrator:`, err);
    }
    return null;
  };

  const fetchUnderwriterDetails = async (underwriterId: number, token: string): Promise<any | null> => {
    try {
      console.log(`  Fetching underwriter details for ID: ${underwriterId}`);
      const endpoints = [
        `${process.env.REACT_APP_API_URL}/api/underwriters/${underwriterId}`,
        `${process.env.REACT_APP_API_URL}/api/underwriter/${underwriterId}`,
      ];
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.status === 200) {
            const underwriter = response.data.data || response.data;
            console.log(`  Underwriter data:`, underwriter);
            return underwriter;
          }
        } catch (e) {
          // Continue to next endpoint
        }
      }
    } catch (err) {
      console.log(`  Failed to fetch underwriter:`, err);
    }
    return null;
  };

  const fetchEmailOptions = async () => {
    try {
      setIsLoadingEmails(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found for fetching email options');
        setIsLoadingEmails(false);
        return;
      }

      const options: { value: string; label: string; type: string }[] = [];

      // 1. Fetch client emails
      try {
        console.log('📧 Fetching client emails for client ID:', clientId);
        const clientEmailsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/client-emails/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const clientEmails = clientEmailsRes.data.data || [];
        console.log(`✅ Found ${clientEmails.length} client emails`);
        clientEmails.forEach((email: ClientEmail) => {
          if (email.email) {
            options.push({
              value: email.email,
              label: `${email.email} (Client)`,
              type: 'Client'
            });
          }
        });
      } catch (err) {
        console.warn('Could not fetch client emails:', err);
      }

      // 2. Fetch persons of interest
      try {
        console.log('👤 Fetching persons of interest for client ID:', clientId);
        const poiResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const persons = Array.isArray(poiResponse.data) ? poiResponse.data : poiResponse.data.data || [];
        console.log(`✅ Found ${persons.length} persons of interest`);
        
        persons.forEach((person: PersonOfInterest) => {
          if (person.email) {
            options.push({
              value: person.email,
              label: `${person.email} (Person Of Interest)`,
              type: 'POI'
            });
          }
        });
      } catch (err) {
        console.warn('Could not fetch persons of interest:', err);
      }

      // 3. Fetch quotes and get administrator/underwriter emails
      try {
        console.log('📄 Fetching quotes for client ID:', clientId);
        const quotesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/client/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const quotes = quotesRes.data.data || quotesRes.data || [];
        console.log(`✅ Found ${quotes.length} quotes`);
        
        if (quotes.length > 0) {
          const sortedQuotes = [...quotes].sort((a, b) => {
            return new Date(b.created_at || b.createdDate || 0).getTime() - 
                   new Date(a.created_at || a.createdDate || 0).getTime();
          });
          
          const latestQuote = sortedQuotes[0];
          console.log('📄 Latest quote:', {
            id: latestQuote.id,
            administrator_id: latestQuote.administrator_id,
            underwriter_id: latestQuote.underwriter_id,
            administrator_ids: latestQuote.administrator_ids,
            underwriter_ids: latestQuote.underwriter_ids
          });
          
          // Try to get administrator email
          let adminEmail: string | null = null;
          let adminName: string = '';
          
          if (latestQuote.administrator_ids && latestQuote.administrator_ids.length > 0) {
            console.log(`👤 Found ${latestQuote.administrator_ids.length} administrator IDs in array`);
            for (const adminId of latestQuote.administrator_ids) {
              console.log(`👤 Fetching administrator ${adminId} from array...`);
              const admin = await fetchAdministratorDetails(adminId, token);
              if (admin) {
                adminEmail = getEmailFromAdmin(admin);
                adminName = getAdminName(admin);
                if (adminEmail) {
                  console.log(`✅ Found administrator email from array: ${adminEmail}`);
                  break;
                }
              }
            }
          }
          
          if (!adminEmail && latestQuote.administrator_id) {
            console.log(`👤 Fetching single administrator ID: ${latestQuote.administrator_id}`);
            const admin = await fetchAdministratorDetails(latestQuote.administrator_id, token);
            if (admin) {
              adminEmail = getEmailFromAdmin(admin);
              adminName = getAdminName(admin);
              if (adminEmail) {
                console.log(`✅ Found administrator email: ${adminEmail}`);
              }
            }
          }
          
          // Add administrator email to options or update existing
          if (adminEmail) {
            console.log(`📝 Processing administrator email: ${adminEmail}`);
            
            const emailValue = adminEmail;
            const existingIndex = options.findIndex(opt => opt.value.toLowerCase() === emailValue.toLowerCase());
            if (existingIndex !== -1) {
              options[existingIndex].label = `${emailValue} (Administrator)`;
              options[existingIndex].type = 'Administrator';
              console.log(`📝 Updated existing email with administrator label: ${emailValue}`);
            } else {
              options.push({
                value: emailValue,
                label: `${emailValue} (Administrator)`,
                type: 'Administrator'
              });
              console.log(`📝 Added new administrator email to options: ${emailValue}`);
            }
            console.log(`📝 Current options count: ${options.length}`);
          } else {
            console.warn('⚠️ No administrator email found for quote:', latestQuote.id);
          }
          
          // Try to get underwriter email
          let underwriterEmail: string | null = null;
          let underwriterName: string = '';
          
          if (latestQuote.underwriter_ids && latestQuote.underwriter_ids.length > 0) {
            console.log(`👤 Found ${latestQuote.underwriter_ids.length} underwriter IDs in array`);
            for (const uwId of latestQuote.underwriter_ids) {
              console.log(`👤 Fetching underwriter ${uwId} from array...`);
              const underwriter = await fetchUnderwriterDetails(uwId, token);
              if (underwriter) {
                const possibleEmailFields = ['email', 'consultantEmail', 'consultant_email', 'underwriterEmail', 'underwriter_email', 'email_address'];
                for (const field of possibleEmailFields) {
                  if (underwriter[field]) {
                    underwriterEmail = underwriter[field];
                    break;
                  }
                }
                underwriterName = underwriter.name || underwriter.consultantName || underwriter.consultant_name || '';
                if (underwriterEmail) {
                  console.log(`✅ Found underwriter email from array: ${underwriterEmail}`);
                  break;
                }
              }
            }
          }
          
          if (!underwriterEmail && latestQuote.underwriter_id) {
            console.log(`👤 Fetching single underwriter ID: ${latestQuote.underwriter_id}`);
            const underwriter = await fetchUnderwriterDetails(latestQuote.underwriter_id, token);
            if (underwriter) {
              const possibleEmailFields = ['email', 'consultantEmail', 'consultant_email', 'underwriterEmail', 'underwriter_email', 'email_address'];
              for (const field of possibleEmailFields) {
                if (underwriter[field]) {
                  underwriterEmail = underwriter[field];
                  break;
                }
              }
              underwriterName = underwriter.name || underwriter.consultantName || underwriter.consultant_name || '';
              if (underwriterEmail) {
                console.log(`✅ Found underwriter email: ${underwriterEmail}`);
              }
            }
          }
          
          if (underwriterEmail) {
            console.log(`📝 Processing underwriter email: ${underwriterEmail}`);
            
            const emailValue = underwriterEmail;
            const existingIndex = options.findIndex(opt => opt.value.toLowerCase() === emailValue.toLowerCase());
            if (existingIndex !== -1) {
              options[existingIndex].label = `${emailValue} (Underwriter)`;
              options[existingIndex].type = 'Underwriter';
              console.log(`📝 Updated existing email with underwriter label: ${emailValue}`);
            } else {
              options.push({
                value: emailValue,
                label: `${emailValue} (Underwriter)`,
                type: 'Underwriter'
              });
              console.log(`📝 Added new underwriter email to options: ${emailValue}`);
            }
            console.log(`📝 Current options count: ${options.length}`);
          } else {
            console.warn('⚠️ No underwriter email found for quote:', latestQuote.id);
          }
        } else {
          console.log('📄 No quotes found for this client');
        }
      } catch (err) {
        console.warn('Could not fetch quote contacts:', err);
      }

      // Remove duplicates but keep different types
      const seen = new Map<string, string>();
      const uniqueOptions = options.filter(option => {
        const key = option.value.toLowerCase().trim();
        if (seen.has(key)) {
          const existingType = seen.get(key);
          if (existingType !== option.type) {
            console.log(`🔍 Same email but different type: ${key} (${existingType} vs ${option.type}) - keeping both`);
            return true;
          }
          console.log(`🔍 Duplicate found: ${key} - removing`);
          return false;
        }
        seen.set(key, option.type);
        return true;
      });

      console.log(`📧 Total unique email options: ${uniqueOptions.length}`);
      console.log('📧 Email options:', uniqueOptions);
      
      setEmailOptions(uniqueOptions);
    } catch (err) {
      console.error('Error fetching email options:', err);
      setError('Failed to load email options');
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const handleDocumentToggle = (docId: number) => {
    setFormData(prev => {
      const selected = prev.selectedDocuments.includes(docId);
      if (selected) {
        return {
          ...prev,
          selectedDocuments: prev.selectedDocuments.filter(id => id !== docId)
        };
      } else {
        return {
          ...prev,
          selectedDocuments: [...prev.selectedDocuments, docId]
        };
      }
    });
  };

  // Handle adding a recipient from the dropdown
  const handleAddRecipient = () => {
    const email = formData.sent_to.trim();
    if (!email) {
      setError('Please select an email address from the dropdown');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setFormData(prev => ({ ...prev, sent_to: '' }));
    setError(null);
  };

  // Handle adding a recipient from the create recipient field
  const handleAddCreateRecipient = () => {
    const email = formData.create_recipient.trim();
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setFormData(prev => ({ ...prev, create_recipient: '' }));
    setError(null);
  };

  // Handle removing a recipient
  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  // Handle forward checkbox toggle
  const handleForwardToggle = (emailId: number) => {
    setSelectedForForward(prev => {
      if (prev.includes(emailId)) {
        return prev.filter(id => id !== emailId);
      } else {
        return [...prev, emailId];
      }
    });
  };

  // Handle view email
  const handleViewEmail = (email: EmailCommunication) => {
    setSelectedEmail(email);
    setShowPreviewModal(true);
  };

  // Handle forward selected emails
  const handleForwardSelected = () => {
    if (selectedForForward.length === 0) {
      setError('Please select at least one email to forward');
      return;
    }
    
    // Find the selected emails
    const selectedEmails = emails.filter(email => selectedForForward.includes(email.id));
    
    if (selectedEmails.length > 0) {
      const firstEmail = selectedEmails[0];
      const subjectPrefix = firstEmail.subject ? `FWD: ${firstEmail.subject}` : 'FWD: ';
      
      // Generate a reference number for the forwarded email attachment
      const forwardRefNo = generateRefNo();
      
      // Create a plain text version of the email for the attachment
      const emailContent = `
  From: ${firstEmail.sent_to}
  Date: ${new Date(firstEmail.date).toLocaleString()}
  Subject: ${firstEmail.subject || ''}
  Message:
  ${firstEmail.message}
      `.trim();
      
      // Convert the email content to a blob and create a File object
      const blob = new Blob([emailContent], { type: 'text/plain' });
      const file = new File([blob], `Forwarded_Email_${forwardRefNo}.txt`, { type: 'text/plain' });
      
      // Store the attachment in a state or directly in the form data
      // We'll use a new state variable for forward attachments
      setForwardAttachments([file]);
      
      setFormData(prev => ({
        ...prev,
        subject: subjectPrefix,
        message: `Please find the forwarded email attached.`,
        sent_to: '',
        create_recipient: ''
      }));
      
      setRecipients([]); // Clear recipients
      setShowCreateModal(true);
      setError(null);
      setSelectedForForward([]);
    }
  };

  const handleCreateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) {
      console.warn('⚠️ Already submitting, ignoring duplicate click');
      return;
    }
    
    // Validation
    const hasSelectedRecipient = formData.sent_to.trim() !== '';
    const hasAddedRecipients = recipients.length > 0;
    
    if (!hasSelectedRecipient && !hasAddedRecipients) {
      setError('Please select a recipient from "Send To" or add recipients using "Create Recipient"');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }

    // Validate document count
    const selectedDocs = clientDocuments.filter(doc => 
      formData.selectedDocuments.includes(doc.id)
    );

    if (selectedDocs.length > MAX_DOCUMENTS) {
      setError(`Too many documents selected. Maximum ${MAX_DOCUMENTS} documents allowed.`);
      return;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;

    const generatedRefNo = generateRefNo();

    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsSubmitting(false);
        return;
      }

      // Validate documents exist
      const validDocs = selectedDocs.filter(doc => doc && doc.id && (doc.file_name || doc.original_name));
      if (validDocs.length !== selectedDocs.length) {
        console.warn(`⚠️ Filtered out ${selectedDocs.length - validDocs.length} invalid documents`);
      }

      console.log('📄 Selected documents for email:', validDocs);
console.log('📄 Document IDs being processed:', validDocs.map((d: any) => d.id));
console.log('📄 Is instruction context:', isInstructionContext);

      // If no valid documents, proceed without attachments
      if (validDocs.length === 0) {
        console.log('📎 No valid documents to attach, proceeding without attachments');
      }

      let allRecipients = [...recipients];
      if (formData.sent_to.trim() && !allRecipients.includes(formData.sent_to.trim())) {
        allRecipients.push(formData.sent_to.trim());
      }

      const formDataPayload = new FormData();
      formDataPayload.append('to', JSON.stringify(allRecipients));
      formDataPayload.append('subject', formData.subject.trim());
      formDataPayload.append('message', formData.message.trim());
      formDataPayload.append('client_id', clientId);
      formDataPayload.append('ref_no', generatedRefNo);
      formDataPayload.append('employee', loggedInUser);
      // document_ids will be appended once here
      formDataPayload.append('document_ids', JSON.stringify(formData.selectedDocuments));
      // Initialize documents as empty - will be updated after download
      formDataPayload.append('documents', JSON.stringify([]));
      
      if (isInstructionContext && instructionId) {
        formDataPayload.append('instruction_id', instructionId);
      }

      // Set progress tracking
      const totalFiles = validDocs.length + forwardAttachments.length;
      setUploadProgress({ current: 0, total: totalFiles });

      // Track successfully downloaded files to only include them
      let successfullyDownloadedDocs: any[] = [];

      for (let i = 0; i < validDocs.length; i++) {
        const doc = validDocs[i];
        
        // Validate document
        if (!doc || !doc.id) {
          console.warn(`⚠️ Invalid document at index ${i}, skipping...`);
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          continue;
        }

        console.log(`📎 Processing document ${i + 1}/${validDocs.length}: ${doc.original_name || doc.file_name} (ID: ${doc.id})`);

        // Skip if no file path
        if (!doc.file_path || doc.file_path.trim() === '') {
          console.warn(`⚠️ Document ${doc.id} has no file path, skipping...`);
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
          continue;
        }

        // Try to download with a single attempt and short timeout
        try {
  // Use the appropriate endpoint based on context
const downloadUrl = isInstructionContext 
  ? `${process.env.REACT_APP_API_URL}/api/instruction-documents/download/${doc.id}`
  : `${process.env.REACT_APP_API_URL}/api/client-documents/download/${doc.id}`;
    
console.log(`📎 Attempting to download document ${doc.id}...`);
console.log(`📎 isInstructionContext: ${isInstructionContext}`);
console.log(`📎 URL: ${downloadUrl}`);
console.log(`📎 Document details:`, { id: doc.id, file_name: doc.file_name, original_name: doc.original_name });

  const response = await axios.get(
    downloadUrl,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
              timeout: 30000, // 30 seconds max
              maxContentLength: Infinity,
              maxBodyLength: Infinity
            }
          );
          
          if (!response || !response.data) {
            console.warn(`⚠️ No data received for document ${doc.id}, skipping...`);
            setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
            continue;
          }

          const fileBlob = response.data;
          
          if (!(fileBlob instanceof Blob) || fileBlob.size === 0) {
            console.warn(`⚠️ Invalid or empty file for document ${doc.id}, skipping...`);
            setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
            continue;
          }

          const fileName = doc.original_name || doc.file_name || 'document.pdf';
          console.log(`✅ Downloaded: ${fileName} (${fileBlob.size} bytes)`);

          // Validate file size (max 25MB)
          const MAX_FILE_SIZE = 25 * 1024 * 1024;
          if (fileBlob.size > MAX_FILE_SIZE) {
            console.warn(`⚠️ File ${fileName} is ${formatFileSize(fileBlob.size)}, exceeding limit, skipping...`);
            setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
            continue;
          }

          // Validate PDF if applicable
          if (fileName.toLowerCase().endsWith('.pdf')) {
            const reader = new FileReader();
            const signatureCheck = await new Promise<string>((resolve) => {
              reader.onload = (e) => {
                const result = e.target?.result as ArrayBuffer;
                const uint8Array = new Uint8Array(result);
                const signature = Array.from(uint8Array.slice(0, 5))
                  .map(b => String.fromCharCode(b))
                  .join('');
                resolve(signature);
              };
              reader.readAsArrayBuffer(fileBlob.slice(0, 10));
            });
            
            if (signatureCheck !== '%PDF-') {
              console.warn(`⚠️ File ${fileName} is not a valid PDF, skipping...`);
              setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
              continue;
            }
          }

          // Append the file to form data
          formDataPayload.append('files', fileBlob, fileName);
          console.log(`✅ Added file to email: ${fileName}`);
          
          // Track this document as successfully downloaded
          successfullyDownloadedDocs.push(doc);

        } catch (err: any) {
          // Handle specific error codes
          if (err.response?.status === 404) {
            console.warn(`⚠️ Document ${doc.id} not found on server (404), skipping...`);
          } else if (err.code === 'ECONNABORTED') {
            console.warn(`⚠️ Download timeout for document ${doc.id}, skipping...`);
          } else {
            console.warn(`⚠️ Failed to download document ${doc.id}:`, err.message || 'Unknown error');
          }
          // Skip this document and continue
        } finally {
          setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
        }
      }

      // Only add document metadata for successfully downloaded files
      // Use set() to replace the initial empty array
      if (successfullyDownloadedDocs.length > 0) {
        const documentsData = successfullyDownloadedDocs.map(doc => ({
          id: doc.id,
          file_name: doc.file_name,
          original_name: doc.original_name,
          file_path: doc.file_path
        }));
        formDataPayload.set('documents', JSON.stringify(documentsData));
        console.log(`✅ Added ${successfullyDownloadedDocs.length} document(s) to metadata`);
      } else {
        // No documents were successfully downloaded, keep empty array
        formDataPayload.set('documents', JSON.stringify([]));
        console.log('📎 No documents were successfully downloaded');
      }

      // Add forward attachments
      for (let i = 0; i < forwardAttachments.length; i++) {
        const file = forwardAttachments[i];
        formDataPayload.append('files', file, file.name);
        console.log(`✅ Added forward attachment: ${file.name}`);
        setUploadProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null);
      }

      console.log('📧 Sending email with FormData');
      console.log('📧 Recipients:', allRecipients);
      console.log('📧 Number of files:', formDataPayload.getAll('files').length);

      // Send email with timeout
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/email/send-formdata`,
        formDataPayload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minute timeout
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send email');
      }

      // Success - add to list
      const allRecipientsForDisplay = [...recipients];
      if (formData.sent_to.trim() && !allRecipientsForDisplay.includes(formData.sent_to.trim())) {
        allRecipientsForDisplay.push(formData.sent_to.trim());
      }

      // ✅ Include subject and metadata so the UI renders attachments immediately
      const newEmail = {
        id: Date.now(),
        date: new Date().toISOString(),
        sent_to: allRecipientsForDisplay.join(', '),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        employee: loggedInUser,
        delivery_status: 'sent' as EmailCommunication['delivery_status'],
        ref_no: generatedRefNo,
        forward: '',
        metadata: {
          documents: [
            ...successfullyDownloadedDocs.map(doc => ({
              id: doc.id,
              file_name: doc.file_name,
              original_name: doc.original_name
            })),
            // ✅ Include forwarded attachments in the local state
            ...forwardAttachments.map((file, idx) => ({
              id: Date.now() + idx,
              file_name: file.name,
              original_name: file.name
            }))
          ],
          attachment_count: successfullyDownloadedDocs.length + forwardAttachments.length
        }
      };
      setEmails([newEmail, ...emails]);

            setError(null);
      resetForm();
      setShowCreateModal(false);
      setForwardAttachments([]);
      setUploadProgress(null);

      // ✅ Notify parent so the Notes tab re-fetches audit logs
      onEmailChange?.();

      alert(`✅ Email sent successfully to ${allRecipientsForDisplay.length} recipient(s)!`);

    } catch (err: any) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to send email';
      setError(errorMessage);
      if (err.code === 'ECONNABORTED') {
        alert('⏱️ Request timed out. The email may still send, please check your sent folder.');
      } else {
        alert(`❌ ${errorMessage}`);
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  const resetForm = () => {
    console.log('🔄 resetForm called - instructionRef:', instructionRef);
    // Build the message with client name and policy number if in instruction context
    let messageWithDetails = '';
    if (isInstructionContext) {
      if (clientName) {
        messageWithDetails += `Client: ${clientName}\n`;
      }
      if (policyNumber) {
        messageWithDetails += `Policy Number: ${policyNumber}\n`;
      }
      if (messageWithDetails) {
        messageWithDetails += '\n';
      }
    }
    
    setFormData({
      sent_to: '',
      create_recipient: '',
      subject: isInstructionContext && instructionRef ? `[${instructionRef}] ` : '',
      message: messageWithDetails,
      selectedDocuments: [],
      instruction_id: instructionId || ''
    });
    setSelectedForForward([]);
    setRecipients([]);
    setForwardAttachments([]);
    setUploadProgress(null);
  };

  // Add this function after resetForm
  const handleOpenEmailModal = () => {
    console.log('🔍 Opening email modal - instructionRef:', instructionRef);
    console.log('🔍 isInstructionContext:', isInstructionContext);
    
    // Set the form data with instruction reference, client name, and policy number
    if (isInstructionContext && instructionRef) {
      // Build the message with client name and policy number
      let messageWithDetails = '';
      if (clientName) {
        messageWithDetails += `Client: ${clientName}\n`;
      }
      if (policyNumber) {
        messageWithDetails += `Policy Number: ${policyNumber}\n`;
      }
      if (messageWithDetails) {
        messageWithDetails += '\n';
      }
      
      console.log('✅ Setting subject to:', `[${instructionRef}] `);
      console.log('✅ Setting message with client:', clientName);
      console.log('✅ Setting message with policy:', policyNumber);
      
      setFormData({
        sent_to: '',
        create_recipient: '',
        subject: `[${instructionRef}] `,
        message: messageWithDetails,
        selectedDocuments: [],
        instruction_id: instructionId || ''
      });
    } else {
      setFormData({
        sent_to: '',
        create_recipient: '',
        subject: '',
        message: '',
        selectedDocuments: [],
        instruction_id: ''
      });
    }
    
    setRecipients([]);
    setSelectedForForward([]);
    setForwardAttachments([]);
    setShowCreateModal(true);
    setError(null);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowCreateModal(false);
    setError(null);
    setSelectedForForward([]);
    setForwardAttachments([]);
    setUploadProgress(null);
  };

  const getStatusBadgeClass = (status: EmailCommunication['delivery_status']) => {
    const classes = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return classes[status] || classes.pending;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading email communications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Email Communications</h3>
        <div className="flex gap-2">
          {selectedForForward.length > 0 && (
            <button
              onClick={handleForwardSelected}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Forward Selected ({selectedForForward.length})
            </button>
          )}
          <button
            onClick={handleOpenEmailModal}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Send Mail
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {emails.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          No email communications found for this client.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ref No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forward
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(email.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.sent_to}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {email.message || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.employee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(email.delivery_status)}`}>
                      {email.delivery_status.charAt(0).toUpperCase() + email.delivery_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.ref_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleViewEmail(email)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <input
                      type="checkbox"
                      checked={selectedForForward.includes(email.id)}
                      onChange={() => handleForwardToggle(email.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      title="Select this email to forward"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreviewModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Email Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Email Header */}
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">From:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.sent_to}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Date:</span>
                  <span className="text-sm text-gray-900 flex-1">
                    {new Date(selectedEmail.date).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Subject:</span>
                  <span className="text-sm text-gray-900 flex-1 font-medium">
                    {selectedEmail.subject || '-'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Employee:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.employee}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Ref No:</span>
                  <span className="text-sm text-gray-900 flex-1 font-mono">{selectedEmail.ref_no || '-'}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Status:</span>
                  <span className="text-sm flex-1">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedEmail.delivery_status)}`}>
                      {selectedEmail.delivery_status.charAt(0).toUpperCase() + selectedEmail.delivery_status.slice(1)}
                    </span>
                  </span>
                </div>
              </div>

              {/* Email Body */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px] max-h-[300px] overflow-y-auto">
                  {selectedEmail.message || '-'}
                </div>
              </div>

              {/* ✅ NEW: Attached Documents */}
              {selectedEmail.metadata?.documents && selectedEmail.metadata.documents.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Attached Documents
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-md divide-y divide-gray-200">
                    {/* Filter out the duplicate float IDs generated by the backend */}
                    {selectedEmail.metadata.documents
                      .filter((doc, index, self) => 
                        index === self.findIndex((d) => d.file_name === doc.file_name)
                      )
                      .map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-900 truncate" title={doc.original_name || doc.file_name}>
                            {doc.original_name || doc.file_name || 'Attachment'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">Sent with email</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Send Mail</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleCreateEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send To
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select from existing contacts and click Add, or use "Create Recipient" below for a new email address
                </p>
                
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={formData.sent_to}
                      onChange={(e) => setFormData({ ...formData, sent_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                      disabled={isLoadingEmails}
                    >
                      <option value="">Select an email address...</option>
                      {emailOptions.length === 0 && !isLoadingEmails && (
                        <option value="" disabled>No email addresses found</option>
                      )}
                      {emailOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddRecipient}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition-colors whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                
                {emailOptions.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Showing {emailOptions.length} email address(es) from client, POI, and quotes
                  </p>
                )}
              </div>

              {/* Create Recipient Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Create Recipient
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={formData.create_recipient}
                      onChange={(e) => setFormData({ ...formData, create_recipient: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter a new email address"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCreateRecipient();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCreateRecipient}
                    className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition-colors whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enter a new email address not listed above and click Add
                </p>
              </div>

              {/* Recipients List - SINGLE DISPLAY */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({recipients.length})
                </label>
                {recipients.length === 0 ? (
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                    No recipients added yet. Use "Send To" or "Create Recipient" above to add recipients.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    {recipients.map((email, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(email)}
                          className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Email subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Email message content..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Files
                </label>
                {isLoadingDocuments ? (
                  <div className="text-gray-500 text-sm py-2">Loading documents...</div>
                ) : clientDocuments.length === 0 ? (
                  <div className="text-gray-500 text-sm py-2">No documents found for this client.</div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {clientDocuments.map((doc) => (
                      <label key={doc.id} className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.selectedDocuments.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.document_type || 'Document'} • {formatFileSize(doc.file_size || 0)}
                            {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {formData.selectedDocuments.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.selectedDocuments.length} file(s) selected
                  </p>
                )}
              </div>

              {uploadProgress && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading files...</span>
                    <span>{uploadProgress.current} of {uploadProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    uploadProgress ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : 'Sending...'
                  ) : 'Send Mail'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPersonalEmailTab;